/**
 * The property manager's own shell (Property Manager rebuild P1): who gets it, and what the menu and phone bar hold.
 * Decided by role, as the FM Manager's is: the menu is a job description. Finance and admins keep their own shell and
 * get these screens as a group. The server's site scope decides what any of them reads.
 */
import { hasPermFor } from '@/utils/permissions';
import type { UserRole } from '@/types';

type PmUser = { role: UserRole; permissions?: string[] | null } | null | undefined;

export const PM_HOME = '/pm';
export const PM_NO_BUILDING = '/pm/no-building';

export const FINANCE_PERMISSIONS = [
  'finance:approve_payment_runs', 'finance:approve_write_offs', 'finance:approve_deposit_refunds',
  'finance:approve_credit_notes', 'finance:approve_cam_trueups',
] as const;

export function isPmShell(user: PmUser): boolean {
  return user?.role === 'property_manager';
}

export function holdsAnApproveRight(user: PmUser): boolean {
  return !!user && FINANCE_PERMISSIONS.some((p) => hasPermFor(user, p));
}

export interface PmNavItem { id: string; label: string; path: string }
export interface PmNavGroup { id: string; heading: string; items: PmNavItem[] }

export const PM_NAV_GROUPS: readonly PmNavGroup[] = [
  { id: 'today', heading: 'Today', items: [
    { id: 'home', label: 'Home', path: '/pm' },
    { id: 'calendar', label: 'Calendar', path: '/pm/calendar' },
    { id: 'alerts', label: 'Alerts', path: '/notifications' },
  ] },
  { id: 'leasing', heading: 'Leasing', items: [
    { id: 'rentroll', label: 'Rent roll', path: '/pm/rent-roll' },
    { id: 'tenants', label: 'Tenants', path: '/pm/tenants' },
    { id: 'moves', label: 'Move-in / out', path: '/pm/moves' },
  ] },
  { id: 'money', heading: 'Money', items: [
    { id: 'billing', label: 'Billing', path: '/pm/billing' },
    { id: 'collections', label: 'Collections', path: '/pm/collections' },
    { id: 'deposits', label: 'Deposits', path: '/pm/deposits' },
    { id: 'cam', label: 'CAM', path: '/pm/cam' },
    { id: 'approvals', label: 'Approvals', path: '/pm/approvals' },
  ] },
  { id: 'buildings', heading: 'Buildings', items: [
    { id: 'compliance', label: 'Compliance', path: '/pm/compliance' },
    { id: 'fm', label: 'FM view (read-only)', path: '/pm/fm' },
  ] },
  { id: 'reports', heading: 'Reports', items: [
    { id: 'owner', label: 'Owner report', path: '/pm/owner-report' },
    { id: 'exports', label: 'Exports for accounts', path: '/pm/exports' },
  ] },
  { id: 'me', heading: 'Me', items: [
    { id: 'profile', label: 'Profile', path: '/pm/profile' },
  ] },
];

/** For Finance and admins beside their usual menu. */
export const FINANCE_GROUP: PmNavGroup = { id: 'finance', heading: 'Landlord finance', items: [
  { id: 'approvals', label: 'Approvals', path: '/pm/approvals' },
  { id: 'collections', label: 'Collections', path: '/pm/collections' },
  { id: 'deposits', label: 'Deposits', path: '/pm/deposits' },
  { id: 'cam', label: 'CAM', path: '/pm/cam' },
  { id: 'billing', label: 'Billing', path: '/pm/billing' },
  { id: 'owner', label: 'Owner report', path: '/pm/owner-report' },
  { id: 'exports', label: 'Exports for accounts', path: '/pm/exports' },
] };

/** Phone bar: Home, Rent roll, Collections, Calendar, Menu (null opens the drawer). */
export const PM_BOTTOM_NAV: readonly { id: string; label: string; path: string | null }[] = [
  { id: 'home', label: 'Home', path: '/pm' },
  { id: 'rentroll', label: 'Rent roll', path: '/pm/rent-roll' },
  { id: 'collections', label: 'Collections', path: '/pm/collections' },
  { id: 'calendar', label: 'Calendar', path: '/pm/calendar' },
  { id: 'menu', label: 'Menu', path: null },
];

/** The screens a pattern-match opens: every `/pm*` route. */
export const PM_ROUTES = [
  '/pm', '/pm/calendar', '/pm/rent-roll', '/pm/tenancies/new', '/pm/tenancies/:id', '/pm/tenancies/:id/edit', '/pm/tenants', '/pm/tenants/:id', '/pm/moves', '/pm/billing',
  '/pm/collections', '/pm/deposits', '/pm/cam', '/pm/approvals', '/pm/compliance', '/pm/fm', '/pm/owner-report', '/pm/exports', '/pm/profile',
] as const;
